import React, { useEffect, useState } from 'react';
import { app } from '../../firebase/firebaseConfig';
import { collection, doc, getFirestore, onSnapshot, setDoc, deleteField, updateDoc } from 'firebase/firestore';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
  ArrowLeft,
  SlidersHorizontal,
  Save,
  X,
  Info,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

// Admin override editor for ONE entity in ONE scope — see settingsScopes.js.
// `entity` is { id, name }: a family (Hearth) or an igloo (Higlo) depending on
// `scope`. Both are required and have no defaults, so a missing one fails loudly
// rather than silently editing the other stack's collection.
function OverrideSettingsViewer({ scope, entity, onBack }) {
  const [internalSettings, setInternalSettings] = useState({});
  const [externalSettings, setExternalSettings] = useState({});
  const [overrideSettings, setOverrideSettings] = useState({});
  const [settingsByCategory, setSettingsByCategory] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [editingCells, setEditingCells] = useState(new Set());
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

  const { globalRoot, internalDoc, externalDoc, overrideRoot } = scope;
  const entityID = entity?.id;
  const entityName = entity?.name || entityID;

  // Listen to global settings (both visibility scopes)
  useEffect(() => {
    const db = getFirestore(app);
    const internalRef = collection(db, globalRoot, internalDoc, 'settings');
    const externalRef = collection(db, globalRoot, externalDoc, 'settings');

    const unsubInternal = onSnapshot(internalRef, (snapshot) => {
      const settings = {};
      snapshot.docs.forEach(d => { settings[d.id] = d.data(); });
      setInternalSettings(settings);
    }, (error) => {
      console.error('OverrideSettingsViewer: internal settings error:', error);
    });

    const unsubExternal = onSnapshot(externalRef, (snapshot) => {
      const settings = {};
      snapshot.docs.forEach(d => { settings[d.id] = d.data(); });
      setExternalSettings(settings);
    }, (error) => {
      console.error('OverrideSettingsViewer: external settings error:', error);
    });

    return () => { unsubInternal(); unsubExternal(); };
  }, [globalRoot, internalDoc, externalDoc]);

  // Listen to override settings for this entity
  useEffect(() => {
    if (!entityID) return;
    const db = getFirestore(app);
    const overrideRef = collection(db, overrideRoot, entityID, 'overrideSettings');

    const unsubscribe = onSnapshot(overrideRef, (snapshot) => {
      const overrides = {};
      snapshot.docs.forEach(d => { overrides[d.id] = d.data(); });
      setOverrideSettings(overrides);
    }, (error) => {
      console.error('OverrideSettingsViewer: override settings error:', error);
    });

    return () => unsubscribe();
  }, [overrideRoot, entityID]);

  // Merge global settings into categorised entries
  useEffect(() => {
    const grouped = {};

    // `visibilityDoc` is which of the scope's two global docs this setting was
    // deployed into (display only here — overrides are written per-category).
    const addEntries = (settingsMap, visibilityDoc) => {
      Object.entries(settingsMap).forEach(([category, fields]) => {
        Object.entries(fields).forEach(([key, meta]) => {
          if (meta && typeof meta === 'object' && 'value' in meta) {
            if (!grouped[category]) grouped[category] = { entries: [], order: meta.order ?? 999 };
            grouped[category].entries.push({
              id: `${visibilityDoc}.${category}.${key}`,
              visibilityDoc,
              category,
              key,
              meta,
              order: meta.order ?? 999,
              label: meta.label || key,
              description: meta.description || '',
              tooltip: meta.tooltip || '',
              type: meta.type,
              choices: meta.choices,
              range: meta.range,
              step: meta.step,
              value: meta.value,
            });
          }
        });
      });
    };

    addEntries(internalSettings, internalDoc);
    addEntries(externalSettings, externalDoc);

    for (const category of Object.keys(grouped)) {
      grouped[category].entries.sort((a, b) => a.order - b.order);
    }

    setSettingsByCategory(grouped);
    setCollapsedCategories(prev => {
      if (prev.size > 0) return prev;
      // Start with all collapsed
      return new Set(Object.keys(grouped));
    });
  }, [internalSettings, externalSettings, internalDoc, externalDoc]);

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const isNumericType = (type) => ['int', 'float', 'number'].includes(type);

  const formatValue = (value) => {
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (typeof value === 'number') return value.toString();
    return String(value);
  };

  // Check if a setting has an active override
  const hasOverride = (category, key) => {
    return overrideSettings[category] !== undefined
      && overrideSettings[category][key] !== undefined;
  };

  const getOverrideValue = (category, key) => {
    return overrideSettings[category]?.[key];
  };

  // Enable an override — write the current global value as the initial override
  const enableOverride = async (category, key, globalValue) => {
    setSaving(true);
    try {
      const db = getFirestore(app);
      const docRef = doc(db, overrideRoot, entityID, 'overrideSettings', category);
      await setDoc(docRef, { [key]: globalValue }, { merge: true });
    } catch (error) {
      console.error('OverrideSettingsViewer: enable override error:', error);
      alert('Failed to enable override. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Disable an override — remove the field from the document
  const disableOverride = async (category, key) => {
    setSaving(true);
    try {
      const db = getFirestore(app);
      const docRef = doc(db, overrideRoot, entityID, 'overrideSettings', category);
      await updateDoc(docRef, { [key]: deleteField() });
    } catch (error) {
      console.error('OverrideSettingsViewer: disable override error:', error);
      alert('Failed to disable override. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleOverride = (category, key, globalValue) => {
    if (hasOverride(category, key)) {
      disableOverride(category, key);
    } else {
      enableOverride(category, key, globalValue);
    }
  };

  // Editing override values
  const startEditing = (cellKey, currentValue) => {
    setEditingCells(prev => new Set(prev).add(cellKey));
    setEditValues(prev => ({ ...prev, [cellKey]: currentValue }));
  };

  const cancelEditing = (cellKey) => {
    setEditingCells(prev => {
      const newSet = new Set(prev);
      newSet.delete(cellKey);
      return newSet;
    });
    setEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[cellKey];
      return newValues;
    });
  };

  const saveOverrideValue = async (cellKey, category, key) => {
    setSaving(true);
    try {
      const newValue = editValues[cellKey];
      const db = getFirestore(app);
      const docRef = doc(db, overrideRoot, entityID, 'overrideSettings', category);
      await setDoc(docRef, { [key]: newValue }, { merge: true });
      cancelEditing(cellKey);
    } catch (error) {
      console.error('OverrideSettingsViewer: save error:', error);
      alert('Failed to save override. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderEditWidget = (cellKey, entry) => {
    const currentEditValue = editValues[cellKey];

    if (entry.choices && entry.choices.length > 0) {
      return (
        <Select
          value={String(currentEditValue)}
          onValueChange={(val) => {
            let parsed = val;
            if (val === 'true') parsed = true;
            else if (val === 'false') parsed = false;
            else if (!isNaN(val) && val !== '') parsed = Number(val);
            setEditValues(prev => ({ ...prev, [cellKey]: parsed }));
          }}
        >
          <SelectTrigger className="h-7 text-xs flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {entry.choices.map((choice, i) => (
              <SelectItem key={i} value={String(choice.value)}>{choice.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (isNumericType(entry.type)) {
      return (
        <Input
          type="number"
          value={currentEditValue ?? ''}
          onChange={(e) => {
            const val = e.target.value === '' ? '' : (entry.type === 'int' ? parseInt(e.target.value) : parseFloat(e.target.value));
            setEditValues(prev => ({ ...prev, [cellKey]: val }));
          }}
          min={entry.range?.[0]}
          max={entry.range?.[1]}
          step={entry.step || (entry.type === 'int' ? 1 : 'any')}
          className="h-7 text-xs flex-1 text-center"
        />
      );
    }

    if (entry.type === 'bool') {
      return (
        <input
          type="checkbox"
          checked={!!currentEditValue}
          onChange={(e) => setEditValues(prev => ({ ...prev, [cellKey]: e.target.checked }))}
          className="h-4 w-4 accent-primary"
        />
      );
    }

    return (
      <Input
        value={currentEditValue ?? ''}
        onChange={(e) => setEditValues(prev => ({ ...prev, [cellKey]: e.target.value }))}
        className="h-7 text-xs flex-1"
        autoFocus
      />
    );
  };

  const categories = Object.keys(settingsByCategory).sort();

  // Count total active overrides
  const totalOverrides = Object.values(overrideSettings).reduce(
    (sum, catOverrides) => sum + Object.keys(catOverrides).length, 0
  );

  return (
    <div className="mx-auto p-6 min-h-screen flex flex-col">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Button>
        <SlidersHorizontal className="w-8 h-8" />
        <div>
          <h1 className="text-3xl font-bold">{scope.overrideTitle}</h1>
          <p className="text-sm text-muted-foreground">{entityName}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        {scope.overrideBlurb}
        {totalOverrides > 0 && (
          <span className="ml-2 font-medium text-foreground">
            {totalOverrides} active override{totalOverrides !== 1 ? 's' : ''}.
          </span>
        )}
      </p>

      {categories.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <SlidersHorizontal className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Loading settings…</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {categories.map(category => {
            const { entries } = settingsByCategory[category];
            const isCollapsed = collapsedCategories.has(category);
            const categoryOverrideCount = overrideSettings[category]
              ? Object.keys(overrideSettings[category]).length
              : 0;

            return (
              <Card key={category}>
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/30 transition-colors"
                >
                  {isCollapsed
                    ? <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                  }
                  <span className="text-lg font-semibold capitalize">{category}</span>
                  <span className="text-sm text-muted-foreground">({entries.length})</span>
                  {categoryOverrideCount > 0 && (
                    <span className="text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                      {categoryOverrideCount} override{categoryOverrideCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </button>

                {!isCollapsed && (
                  <CardContent className="pt-0 pb-2 px-2">
                    <table className="w-full">
                      <thead>
                        {/* DESCRIPTION IS THE ONE FLEXIBLE COLUMN — every other width is
                            fixed, so description absorbs whatever is left. It was the other
                            way round (description pinned at 250px, override value flexible),
                            which read fine for terse device settings but badly for the Higlo
                            ones: their descriptions carry the consequence warnings and run to
                            several lines, so they wrapped into a narrow tower beside a wide
                            empty gap. Override value only ever holds a ~150px edit widget plus
                            two 28px icon buttons, so it has a fixed width sized to exactly
                            that and never needs the slack. */}
                        <tr className="text-xs text-muted-foreground">
                          <th className="py-1 px-2 text-left font-normal w-[200px]">Setting</th>
                          <th className="py-1 px-2 text-left font-normal w-[120px]">Global Value</th>
                          <th className="py-1 px-2 text-center font-normal w-[60px]">Override</th>
                          <th className="py-1 px-2 text-left font-normal w-[230px]">Override Value</th>
                          <th className="py-1 px-2 text-left font-normal">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry, index) => {
                          const isOverridden = hasOverride(entry.category, entry.key);
                          const overrideValue = getOverrideValue(entry.category, entry.key);
                          const cellKey = `override_${entry.category}_${entry.key}`;
                          const isEditing = editingCells.has(cellKey);

                          return (
                            <tr
                              key={entry.id}
                              className={`${index % 2 === 0 ? 'bg-muted/60' : ''} ${isOverridden ? 'bg-blue-50/80' : ''}`}
                            >
                              {/* Setting key */}
                              <td className="py-1.5 px-2 text-sm font-mono whitespace-nowrap align-middle">
                                {entry.key}
                              </td>

                              {/* Global value */}
                              <td className="py-1.5 px-2 text-sm font-mono text-muted-foreground align-middle">
                                {formatValue(entry.value)}
                              </td>

                              {/* Override toggle */}
                              <td className="py-1.5 px-2 text-center align-middle">
                                <Switch
                                  checked={isOverridden}
                                  onCheckedChange={() => toggleOverride(entry.category, entry.key, entry.value)}
                                  disabled={saving}
                                  className="mx-auto"
                                />
                              </td>

                              {/* Override value (editable when active) */}
                              <td className="py-1.5 px-2 align-middle">
                                {isOverridden ? (
                                  <div className="flex items-center gap-1">
                                    {isEditing ? (
                                      <>
                                        <div className="min-w-[150px]">
                                          {renderEditWidget(cellKey, entry)}
                                        </div>
                                        <Button
                                          size="sm"
                                          onClick={() => saveOverrideValue(cellKey, entry.category, entry.key)}
                                          disabled={saving}
                                          className="h-7 w-7 p-0 flex-shrink-0"
                                        >
                                          <Save className="w-3 h-3" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => cancelEditing(cellKey)}
                                          disabled={saving}
                                          className="h-7 w-7 p-0 flex-shrink-0"
                                        >
                                          <X className="w-3 h-3" />
                                        </Button>
                                      </>
                                    ) : (
                                      <>
                                        <span className={`text-sm font-mono ${overrideValue !== entry.value ? 'text-blue-700 font-semibold' : ''}`}>
                                          {formatValue(overrideValue)}
                                        </span>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => startEditing(cellKey, overrideValue)}
                                          className="h-6 w-6 p-1 opacity-60 hover:opacity-100 transition-opacity hover:bg-gray-100 border border-gray-300 bg-white/80 flex-shrink-0"
                                        >
                                          <SlidersHorizontal className="w-3 h-3" />
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>

                              {/* Description */}
                              <td className="py-1.5 px-2 text-xs text-muted-foreground align-middle">
                                <div className="flex items-start gap-1.5">
                                  {/* NOT truncated: a setting's description is where a
                                      consequence warning lives (e.g. "changing this causes
                                      a Higlo reset"), and this editor is where the change
                                      is actually made — clipping it to one line would hide
                                      the warning behind a tooltip hover. */}
                                  <span>{entry.description}</span>
                                  {entry.tooltip && (
                                    <span className="relative group">
                                      <Info className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 cursor-help" />
                                      <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded shadow-lg whitespace-normal min-w-[200px] max-w-md hidden group-hover:block z-20">
                                        {entry.tooltip}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default OverrideSettingsViewer;
